import { useState } from 'react';

type LoginPageProps = { onLoginSuccess: (token: string) => void };

export function LoginPage({ onLoginSuccess }: LoginPageProps) {
  const [user, setUser] = useState('');
  const [pass, setPass] = useState('');
  const [error, setError] = useState<string | null>(null);
  const relayUrl = process.env.REACT_APP_RELAY_SERVER_URL || 'ws://localhost:8081';
  const authUrl = relayUrl.replace(/^ws(s?):\/\//, 'http$1://') + '/auth';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const res = await fetch(authUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user, pass }),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem('authToken', data.token);
        onLoginSuccess(data.token);
      } else {
        setError(data.error || 'Usuário ou senha inválidos.');
      }
    } catch {
      setError('Erro ao conectar ao servidor.');
    }
  };

  // Paleta de cores com base na imagem fornecida
  const COLORS = {
    primary: '#E64424', // Cor do logo na imagem
    primaryDark: '#A93A20', // Cor mais escura
    bgLight: '#F2F2F2', // Fundo claro
    text: '#333333', // Cor do texto
    border: '#E6EEF5', // Cor da borda
    accent: '#F0B12D', // Cor do detalhe laranja
    accentHover: '#D99700', // Cor de hover do botão
    error: '#B00020', // Cor para mensagens de erro
    white: '#FFFFFF', // Cor branca
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: `linear-gradient(180deg, ${COLORS.primary} 0%, ${COLORS.bgLight} 100%)`,
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          width: '100%',
          maxWidth: 380,
          padding: 28,
          borderRadius: 16,
          background: 'rgba(255,255,255,0.88)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          boxShadow: '0 24px 60px rgba(0,0,0,0.18), 0 8px 20px rgba(0,0,0,0.08)',
          border: `1px solid ${COLORS.border}`,
          color: COLORS.text,
          position: 'relative',
        }}
      >
        {/* Detalhe laranja na parte superior */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            width: 64,
            height: 8,
            borderRadius: 999,
            background: COLORS.accent,
            marginTop: -16,
          }}
        />

        <h1 style={{ margin: '0 0 18px', fontSize: 24, color: COLORS.text }}>
          Login
        </h1>

        <label style={{ display: 'block', marginBottom: 10, fontSize: 14 }}>
          Usuário
          <input
            type="text"
            value={user}
            onChange={(e) => setUser(e.target.value)}
            autoComplete="username"
            placeholder="Seu usuário"
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              marginTop: 6,
              outline: 'none',
              background: COLORS.white,
              color: COLORS.text,
              boxShadow: '0 1px 0 rgba(0,0,0,0.02) inset',
            }}
            onFocus={(e) =>
              (e.currentTarget.style.boxShadow =
                `0 0 0 3px ${COLORS.bgLight}`)
            }
            onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
          />
        </label>

        <label style={{ display: 'block', marginBottom: 6, fontSize: 14 }}>
          Senha
          <input
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            autoComplete="current-password"
            placeholder="Sua senha"
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 10,
              border: `1px solid ${COLORS.border}`,
              marginTop: 6,
              outline: 'none',
              background: COLORS.white,
              color: COLORS.text,
              boxShadow: '0 1px 0 rgba(0,0,0,0.02) inset',
            }}
            onFocus={(e) =>
              (e.currentTarget.style.boxShadow =
                `0 0 0 3px ${COLORS.bgLight}`)
            }
            onBlur={(e) => (e.currentTarget.style.boxShadow = 'none')}
          />
        </label>

        {error && (
          <div
            role="alert"
            style={{
              color: COLORS.error,
              background: '#FFE9ED',
              border: '1px solid #ffd6dc',
              borderRadius: 8,
              padding: '8px 10px',
              marginTop: 10,
              marginBottom: 8,
              fontSize: 14,
            }}
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          style={{
            width: '100%',
            padding: '12px 16px',
            borderRadius: 10,
            border: 'none',
            cursor: 'pointer',
            background: COLORS.accent,
            color: '#1C1C1C',
            fontWeight: 600,
            transition: 'transform .04s ease, box-shadow .2s ease, background .2s',
            boxShadow: '0 8px 18px rgba(240,177,45,0.35)',
            marginTop: 10,
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = COLORS.accentHover)
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = COLORS.accent)
          }
          onMouseDown={(e) => (e.currentTarget.style.transform = 'translateY(1px)')}
          onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
        >
          Entrar
        </button>

        {/* Rodapé com mini-bar azul */}
        <div
          aria-hidden
          style={{
            height: 4,
            marginTop: 12,
            borderRadius: 4,
            background: `linear-gradient(90deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
          }}
        />
      </form>
    </div>
  );
}